import dayjs from 'dayjs'
import { sortBy, merge, partition } from 'es-toolkit'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useSelector, useDispatch, shallowEqual } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { Card, CardBody, CardTitle } from 'reactstrap'

import type { TenantHourlyWorkData, TenantHourlyWorkRow } from 'api/dashboard/types'
import { CONNECTION_TYPES } from 'api/schedule_types/constants'
import type { ConnectionType } from 'api/schedule_types/types'

import { selectDashboardStatus, getTenantSummary, getWorkerCounts } from 'slices/dashboardSlice'
import { showError, showSuccess } from 'slices/notificationSlice'
import { selectTenantsStatus } from 'slices/tenantsSlice'
import { clearErrorMessage, selectUsersStatus, updateDisplayFilter } from 'slices/usersSlice'

import {
  BadgeButton,
  Chart,
  PivotItem,
  PivotOuterIndex,
  NotSelectedPlaceholder,
  GroupedGraphSelectButton,
} from 'components/common'
import { PLACE_HOLDER_TYPES } from 'components/common/NotSelectedPlaceholder/NotSelectedPlaceholder'
import type { BadgeItem, Series } from 'components/common/types'
import { createLineChartOptions, createBarChartOptions, getMaxYAxisValue } from 'components/common/utils'

import useBusinessTime from 'hooks/useBusinessTime'
import { useChart } from 'hooks/useChart'
import useDateChange from 'hooks/useDateChange'
import useDateQuery from 'hooks/useDateQuery'

import Dashboard from './Dashboard'
import WorkerCountsCard from './WorkerCountsCard'
import { calculateProductivityMetrics, colorTypeToCode, createXAxis } from './utils'

import styles from './TotalSummary.module.scss'
const GRAPH_TYPES = {
  LINE: 'line',
  BAR: 'bar',
} as const

import type { XAxisOptions } from 'highcharts'

const graphList = [
  { header: '累計グラフ', icon: 'amount', key: 'total', type: GRAPH_TYPES.LINE },
  { header: '時間別グラフ', icon: 'by_time', key: 'time', type: GRAPH_TYPES.LINE },
  { header: '目標達成率', icon: 'percent', key: 'rate', type: GRAPH_TYPES.LINE },
  { header: '過不足人時', icon: 'replace', key: 'forecastShortfallHour', type: GRAPH_TYPES.BAR },
]

// createRows用のnullを含む足し算. aもbもnullのときにはnullを返す
const nullAddition = (a: number | null, b: number | null) => {
  if (a === null && b === null) {
    return null
  }
  return (a || 0) + (b || 0)
}
const createRows = (data: TenantHourlyWorkData | undefined, graphPivotIndex: number): TenantHourlyWorkRow[] => {
  if (!data) {
    return []
  }
  if (graphPivotIndex === 0 || graphPivotIndex === 2) {
    return data.data.map(d => ({ ...d, time: dayjs(d.time).format() }))
  }
  return data.data.reduce<TenantHourlyWorkRow[]>((acc, cur) => {
    // 15, 30, 45 分を切り捨てた時刻
    const flooredTime = dayjs(cur.time).startOf('hour').utc().format()

    // 正時のデータに集約する
    const target = acc.find(t => t.time === flooredTime)
    if (!target) {
      return acc.concat({ ...cur, time: flooredTime })
    }
    target.planCount = target.planCount + cur.planCount
    target.recordCount = nullAddition(target.recordCount, cur.recordCount)
    return acc
  }, [])
}

type SummaryBadgeItem = BadgeItem & {
  unit: string
  connectionType: ConnectionType
}

const TotalSummary = () => {
  const dispatch = useDispatch()

  const { checkTenantCreated } = useDateChange()
  const [graphPivotIndex, setGraphPivotIndex] = useState(0)
  const [submitted, setSubmitted] = useState(false)
  const [selectedGraphs, setSelectedGraphs] = useState<number[]>([])

  const navigate = useNavigate()

  const { tenant } = useSelector(selectTenantsStatus, shallowEqual)
  const { tenantSummary } = useSelector(selectDashboardStatus, shallowEqual)
  const { displayFilter, errorMessage, isRequesting } = useSelector(selectUsersStatus, shallowEqual)
  const { tenantWithDate } = useSelector(selectTenantsStatus, shallowEqual)

  const { getTimeOver24h, businessStartTime, businessEndTime } = useBusinessTime({ tenantWithDate })
  const date = useDateQuery()
  const { tooltipFormatter, tooltipFormatterWithNextTime } = useChart()

  const graphBadges = useMemo(() => {
    if (!tenantSummary?.hourlyWorkData) {
      return []
    }
    return sortBy(tenantSummary.hourlyWorkData, ['workspaceName', 'scheduleTypeName'])
      .filter(data => selectedGraphs.includes(data.scheduleTypeId))
      .map<SummaryBadgeItem>(data => ({
        color: data.scheduleTypeColor,
        key: data.scheduleTypeId,
        label: `${data.workspaceName}/${data.scheduleTypeName}`,
        unit: data.unit,
        connectionType: data.connectionType,
        disabled: true,
      }))
  }, [selectedGraphs, tenantSummary?.hourlyWorkData])

  useEffect(() => {
    dispatch(getWorkerCounts(date))
    dispatch(getTenantSummary(date, { dashboardFilter: true }))
  }, [dispatch, date])

  useEffect(() => {
    // 選択されている日付が変更された時、displayFilterからselectedCardsを作成し直す
    setSelectedGraphs(
      displayFilter?.dashboard.workspaceData
        .flatMap(df => df.scheduleTypeData)
        .filter(scheduleType => scheduleType.isFilteredInSummary)
        .map(scheduleType => scheduleType.id) || []
    )
  }, [date, displayFilter?.dashboard])

  useEffect(() => {
    if (!submitted || isRequesting) {
      return
    }
    if (errorMessage === '') {
      dispatch(showSuccess())
    } else {
      dispatch(showError())
      dispatch(clearErrorMessage())
    }
    setSubmitted(false)
  }, [submitted, isRequesting, errorMessage, dispatch])

  const selectedBadges = useMemo(
    () => graphBadges.filter(badge => selectedGraphs.some(key => key === badge.key)),
    [graphBadges, selectedGraphs]
  )
  const existTargetAchievementRateGraph = useMemo(() => {
    if (!tenantSummary) {
      return false
    }

    const selectedBadgeKeys = selectedBadges.map(badge => badge.key)
    const targetCountData = tenantSummary.workspaceData
      .flatMap(({ data }) => data)
      .filter(data => selectedBadgeKeys.includes(data.scheduleTypeId))

    return tenantSummary.hourlyWorkData
      .filter(
        data =>
          selectedBadgeKeys.includes(data.scheduleTypeId) &&
          targetCountData.find(tcData => data.scheduleTypeId === tcData.scheduleTypeId)?.targetCount
      )
      .some(hwData => hwData.data.some(data => data.cumulativePlanCount || data.cumulativeRecordCount))
  }, [tenantSummary, selectedBadges])

  const barChartOptions = useMemo(() => {
    const formattedWorkspaceData =
      tenantSummary?.workspaceData
        .flatMap(w => w.data)
        .filter(s => selectedBadges.some(badge => badge.key === s.scheduleTypeId))
        .map(data => {
          const { forecastShortfallHour } = calculateProductivityMetrics(data)
          const label = selectedBadges.find(badge => badge.key === data.scheduleTypeId)?.label || ''
          return { data: forecastShortfallHour, label }
        }) || []
    const [workspaceWithoutData, workspaceWithData] = partition(formattedWorkspaceData, o => !o.data)
    const barGraphData = sortBy(workspaceWithoutData, ['data']).concat(sortBy(workspaceWithData, ['data']))
    const handleGraphClick = (label: string) => {
      // barGraphDataでlabelにselectedBadges.labelを渡しているのでselectedBadgesからscheduleTypeIdを取得する
      const selectedScheduleTypeId = selectedBadges.find(badge => badge.label === label)?.key
      const selectedWorkspaceId = tenantSummary?.workspaceData.find(data =>
        data.data.some(d => d.scheduleTypeId === selectedScheduleTypeId)
      )?.workspaceId
      if (selectedWorkspaceId) {
        navigate(`/dashboard/${selectedWorkspaceId}`)
      }
    }

    const options = createBarChartOptions(barGraphData, handleGraphClick)
    options.tooltip!.formatter = function () {
      return `
          <div style="text-align:center">${this.x}<br>${(this.y || 0) < 0 ? this.y : '+' + this.y}人時</div>`
    }
    return options
  }, [tenantSummary?.workspaceData, selectedBadges, navigate])

  const lineChartOptions = useMemo(() => {
    if (!tenant || !tenantSummary) {
      return {}
    }

    const workspaceDataArray = tenantSummary ? tenantSummary.workspaceData : []
    const hourlyWorkDataArray = tenantSummary ? tenantSummary.hourlyWorkData : []

    const xAxisData = createXAxis(businessStartTime, businessEndTime, graphPivotIndex !== 1, date)
    const currentTime = dayjs()
    const currentTimeIndex = xAxisData.findIndex(x => dayjs(x).isAfter(currentTime)) - 1
    const isBusinessTime = currentTimeIndex >= -1

    const yAxis: Series[] = []
    selectedBadges.forEach(graphBadge => {
      const colorCode = colorTypeToCode(graphBadge.color)
      const hourlyWorkData = hourlyWorkDataArray.find(data => data.scheduleTypeId === graphBadge.key)
      const rows = createRows(hourlyWorkData, graphPivotIndex)

      const targetCount =
        workspaceDataArray.flatMap(({ data }) => data).find(data => data.scheduleTypeId === graphBadge.key)
          ?.targetCount || null

      const unit = graphPivotIndex === 2 ? '%' : graphBadge.unit ?? ''
      const lastTargetIndex = xAxisData
        .slice()
        .reverse()
        .findIndex(time => {
          const reversedRows = rows.slice().reverse()
          const target = reversedRows.find(row => {
            return row.recordCount && row.recordCount > 0
          })
          if (!target) {
            return false
          }
          return new Date(target.time).getTime() === new Date(time).getTime()
        })
      // areaの上にlineを表示するためにareaを先にしておく
      const recordData = xAxisData.map((time, index) => {
        // 現在時刻以降のデータは表示しない
        // 手動実績の場合は現在時刻以降にもデータが存在する場合のみ表示する
        if (dayjs(date).isSameOrAfter(currentTime, 'day') && isBusinessTime && index > currentTimeIndex) {
          if (graphBadge.connectionType !== CONNECTION_TYPES.MANUAL) {
            return null
          }
          if (graphPivotIndex === 1 && index > lastTargetIndex) {
            return null
          }
          const beforeTarget = rows.find(row => {
            const oneHourIndex = 4
            return (
              index > oneHourIndex &&
              new Date(row.time).getTime() === new Date(xAxisData[index - oneHourIndex]).getTime()
            )
          })
          if (beforeTarget && beforeTarget.recordCount === 0) {
            return null
          }
        }

        // パフォーマンス改善のために dayjs でなく new Date を使う
        const target = rows.find(row => new Date(row.time).getTime() === new Date(time).getTime())
        if (!target) {
          return null
        }
        if (graphPivotIndex === 0) {
          return target.cumulativeRecordCount
        }
        if (graphPivotIndex === 1) {
          return target.recordCount
        }
        return target.cumulativeRecordCount && targetCount && (target.cumulativeRecordCount * 100) / targetCount
      })
      yAxis.push({
        type: 'area',
        color: colorCode,
        data: recordData,
        name: graphBadge.label,
        custom: {
          unit,
        },
      })

      const planData = xAxisData.map(time => {
        // パフォーマンス改善のために dayjs でなく new Date を使う
        const target = rows.find(row => new Date(row.time).getTime() === new Date(time).getTime())
        if (!target) {
          return null
        }
        if (graphPivotIndex === 0) {
          return target.cumulativePlanCount
        }
        if (graphPivotIndex === 1) {
          return target.planCount
        }
        return target.cumulativePlanCount && targetCount && (target.cumulativePlanCount * 100) / targetCount
      })
      const firstDataIndex = planData.findIndex(d => d !== null)
      yAxis.push({
        type: 'line',
        color: colorCode,
        data: planData,
        name: graphBadge.label,
        custom: {
          unit,
          firstDataIndex,
        },
      })
    })

    const options = createLineChartOptions({
      xAxis: {
        data: xAxisData,
      },
      yAxis,
    })
    merge(options, {
      xAxis: {
        labels: {
          step: graphPivotIndex === 1 ? 2 : 8,
        },
        tickInterval: 1,
      },
    })
    ;(options.xAxis as XAxisOptions).labels!.formatter = function () {
      if (this.isFirst) {
        return dayjs(this.value).format('HH:mm')
      }
      return getTimeOver24h(dayjs(this.value).format('HH:mm'))
    }
    //目標達成率が選択されているとき、100の基準線を表示
    if (graphPivotIndex === 2) {
      const maxValue = getMaxYAxisValue(yAxis)
      // 100だとラベルが見切れるため、110で設定する
      const defaultMaxValue = 110
      merge(options, {
        yAxis: {
          plotLines: [
            {
              value: 100,
              color: 'var(--bs-primary)',
              zIndex: 1,
              width: 1,
              label: {
                text: '100%',
                align: 'left',
                y: -5,
                x: 0,
                style: {
                  color: 'var(--bs-primary)',
                },
              },
            },
          ],
          max: maxValue < defaultMaxValue ? defaultMaxValue : maxValue,
        },
      })
    }
    options.tooltip!.formatter = graphPivotIndex === 1 ? tooltipFormatterWithNextTime : tooltipFormatter

    return options
  }, [
    tenant,
    tenantSummary,
    businessStartTime,
    businessEndTime,
    graphPivotIndex,
    date,
    selectedBadges,
    tooltipFormatterWithNextTime,
    tooltipFormatter,
    getTimeOver24h,
  ])

  const lastImportedAt = useMemo(() => {
    const importedAt = tenantSummary?.hourlyWorkData[0]?.lastImportedAt
    return importedAt && dayjs(importedAt).format('YYYY/MM/DD HH:mm:ss')
  }, [tenantSummary])

  const handleSaveButtonClick = () => {
    if (!displayFilter?.dashboard) {
      dispatch(showError())
      return
    }
    setSubmitted(true)
    const workspaceData = displayFilter.dashboard.workspaceData.map(df => {
      const scheduleTypeData = df.scheduleTypeData.map(scheduleType => ({
        ...scheduleType,
        isFilteredInSummary: selectedGraphs.includes(scheduleType.id),
      }))
      return { id: df.id, scheduleTypeData }
    })

    dispatch(updateDisplayFilter({ dashboard: { workspaceData } }))
  }

  const graphItems = useMemo(() => {
    if (!tenantSummary?.workspaceData) {
      return []
    }
    const formattedWorkspaceData = tenantSummary.workspaceData
      .filter(o => o.data.length > 0)
      .map(data => {
        const filterItems = sortBy(data.data, ['scheduleTypeName']).map(s => ({
          key: s.scheduleTypeId,
          label: s.scheduleTypeName,
          checked: selectedGraphs.includes(s.scheduleTypeId),
        }))
        return {
          label: data.workspaceName,
          filterItems,
        }
      })
    return sortBy(formattedWorkspaceData, ['label'])
  }, [tenantSummary?.workspaceData, selectedGraphs])

  const disabled = useMemo(() => tenantSummary?.workspaceData.length === 0, [tenantSummary])

  const handleSelectedGraphsChange = (filterItems: number[]) => {
    const addiction = filterItems.filter(s => !selectedGraphs.includes(s))
    const existAddiction = addiction.length > 0
    if (existAddiction) {
      addiction.forEach(scheduleTypeId => dispatch(getTenantSummary(date, { scheduleTypeId })))
    }
    setSelectedGraphs(filterItems)
  }

  const chartOptions = useMemo(
    () => (graphList[graphPivotIndex].type === GRAPH_TYPES.BAR ? barChartOptions : lineChartOptions),
    [graphPivotIndex, barChartOptions, lineChartOptions]
  )

  const handleDateChange = useCallback((newDate: string) => checkTenantCreated(newDate), [checkTenantCreated])

  return (
    <Dashboard
      onInterval={() => selectedGraphs.forEach(scheduleTypeId => dispatch(getTenantSummary(date, { scheduleTypeId })))}
      onDateChange={handleDateChange}
    >
      <Card>
        <CardBody>
          <div className="d-flex">
            <CardTitle className="fw-bold text-nowrap">作業進捗</CardTitle>
            <div className="w-100 d-flex justify-content-end">
              <GroupedGraphSelectButton
                items={graphItems}
                selectedGraphs={selectedGraphs}
                onChange={filterItems => handleSelectedGraphsChange(filterItems)}
                disabled={disabled}
                text="表示作業"
                onSaveButtonClick={handleSaveButtonClick}
                date={date}
              />
            </div>
          </div>
          {selectedGraphs.length === 0 ? (
            <NotSelectedPlaceholder type={PLACE_HOLDER_TYPES.DASHBOARD} />
          ) : (
            <div>
              <PivotOuterIndex selectedIndex={graphPivotIndex} onChange={setGraphPivotIndex}>
                {graphList.map(({ header, icon, key }) => (
                  <PivotItem headerText={header} icon={icon} key={key} />
                ))}
              </PivotOuterIndex>
              {graphPivotIndex === 2 && !existTargetAchievementRateGraph ? (
                <div className={`d-flex flex-column justify-content-center align-items-center ${styles.noDataView}`}>
                  <div className="fw-bold font-large mb-3">目標が設定されていません</div>
                  <div>目標を設定すると、目標を100%としたグラフを表示することができます。</div>
                </div>
              ) : (
                <Chart options={chartOptions} />
              )}
            </div>
          )}
        </CardBody>

        <CardBody className="d-flex row p-2 mx-0">
          <BadgeButton items={graphBadges} />
        </CardBody>

        <CardBody className="d-flex text-muted align-self-end pt-0">
          {lastImportedAt && <i className="icf-updated align-self-center pe-1" />}
          <small>{lastImportedAt}</small>
        </CardBody>
      </Card>
      <WorkerCountsCard close total date={date} />
    </Dashboard>
  )
}

export default TotalSummary
